import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { API_URL } from '../../../api/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const CashLoanLayer = () => {
    const location = useLocation();
    const [loans, setLoans] = useState([]);
    const navigate = useNavigate();
    const [printLoan, setPrintLoan] = useState(null);
    const [loading, setLoading] = useState(true);
    const queryParams = new URLSearchParams(location.search);
    const branch = queryParams.get('branch');
    const group = queryParams.get('group');
    const day = queryParams.get('day');
    const type = queryParams.get('type');
    const token = localStorage.getItem('token');
    const [selectedLoanForPrint, setSelectedLoanForPrint] = useState(null);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // New states for search inputs
    const [searchMember, setSearchMember] = useState('');
    const [searchMobile, setSearchMobile] = useState('');

    useEffect(() => {
        const fetchLoans = async () => {
            try {
                const endpoint = `${API_URL}/cash-loans-search`;

                const response = await axios.post(endpoint, {
                    branch_id: branch,
                    group_id: group
                });
                setLoans(response.data?.data || response.data);
            } catch (error) {
                console.error('Error fetching report', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLoans();
    }, [branch, group, day, type]);

    // Filter loans by member name and mobile (case-insensitive)
    const filteredLoans = loans.filter(loan => {
        const fullName = `${loan.member?.firstname ?? ''} ${loan.member?.lastname ?? ''}`.toLowerCase();
        const mobile = loan.member?.mobile ?? ''; // Assuming mobile is a member property, change if needed

        return (
            fullName.includes(searchMember.toLowerCase()) &&
            mobile.includes(searchMobile)
        );
    });

    const handleExport = () => {
        const exportData = filteredLoans.map((loan) => ({
            ID: loan.id,
            Member: `${loan.member?.firstname} ${loan.member?.lastname}`,
            Branch: loan.branch?.branchname,
            Staff: loan.staff?.firstname,
            'Loan Amount with Int': loan.loan_amount_with_interest,
            'Loan Amount': loan.loan_amount,
            Balance: loan.balance,
            Group: loan.group?.groupname,
            EMI: Math.round(loan.loan_amount / loan.tenure),
            'Open Date': loan.open_date,
            'Close Date': loan.close_day ? loan.close_day : getCloseDate(loan.open_date, loan.tenure),
            Status: loan.status,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Loans");

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(fileData, 'Loans.xlsx');
    };

    // const handleDelete = async (id) => {
    //     const result = await Swal.fire({
    //         title: 'Are you sure?',
    //         text: 'This will soft delete the loan.',
    //         icon: 'warning',
    //         showCancelButton: true,
    //         confirmButtonText: 'Yes, delete it!',
    //     });

    //     if (result.isConfirmed) {
    //         try {
    //             await axios.delete(`${API_URL}/cash-loans/${id}`);
    //             Swal.fire('Deleted!', 'Loan has been soft deleted.', 'success');
    //             // Optionally refresh list after delete here
    //         } catch (error) {
    //             console.error('Delete failed:', error);
    //             Swal.fire('Error', 'Failed to delete loan', 'error');
    //         }
    //     }
    // };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Cash Loan',
            text: 'Are you sure you want to delete this cash loan?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await axios.delete(`${API_URL}/cash-loans/${id}`);
                    return response.data;
                } catch (error) {
                    Swal.showValidationMessage(
                        `Request failed: ${error.response?.data?.message || error.message}`
                    );
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });

        if (result.isConfirmed && result.value) {
            if (result.value.success) {
                // Show success message
                Swal.fire({
                    title: 'Deleted!',
                    text: result.value.message || 'Cash loan has been deleted successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    timer: 2000,
                    timerProgressBar: true
                });

                // Remove the deleted loan from state
                const updatedLoans = loans.filter(loan => loan.id !== id);
                setLoans(updatedLoans);

            } else {
                // Show error message from API
                Swal.fire({
                    title: 'Cannot Delete',
                    html: `
                    <div style="text-align: left;">
                        <p><strong>${result.value.message || 'Loan cannot be deleted.'}</strong></p>
                        ${result.value.remaining_balance ?
                            `<p><strong>Outstanding Balance:</strong> ₹${result.value.remaining_balance}</p>` : ''}
                        ${result.value.loan_amount ?
                            `<p><strong>Original Loan Amount:</strong> ₹${result.value.loan_amount}</p>` : ''}
                        ${result.value.total_paid ?
                            `<p><strong>Total Paid:</strong> ₹${result.value.total_paid}</p>` : ''}
                    </div>
                `,
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3085d6'
                });
            }
        } else if (result.isConfirmed && !result.value) {
            // Network or other error
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete loan. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const getCloseDate = (openDate, tenureInWeeks) => {
        if (!openDate || !tenureInWeeks) return '-';
        const open = new Date(openDate);
        open.setDate(open.getDate() + tenureInWeeks * 7); // 7 days in a week
        return open.toISOString().split('T')[0]; // Return date in YYYY-MM-DD format
    };

    const handlePrintClick = (loan) => {
        setPrintLoan(loan);
    };

    const handleClosePrintView = () => {
        setPrintLoan(null);
    };

    const handlePrintDocument = () => {
        window.print();
    };


    const handleStatementPrintClick = (loan) => {
        setSelectedLoanForPrint(loan);
    };

    const handleCloseStatementView = () => {
        setSelectedLoanForPrint(null);
    };

    const handleStatementPrintDocument = () => {
        window.print();
    };


    if (loading) {
        return <p>Loading loans...</p>;
    }
    let sortedPayments = [];
    let totalPaid = 0;

    if (selectedLoanForPrint) {
        sortedPayments = [...(selectedLoanForPrint.payments || [])].sort(
            (a, b) => new Date(a.payment_date) - new Date(b.payment_date)
        );
    }
    return (
        <div className="row gy-4">
            <div className="col-md-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="card-title mb-0">Cash Loan</h5>

                        <div className="d-flex gap-2 flex-grow-1 justify-content-center">
                            <input
                                type="text"
                                placeholder="Search by Member Name"
                                className="form-control form-control-sm"
                                value={searchMember}
                                onChange={e => setSearchMember(e.target.value)}
                            />
                            <input
                                type="text"
                                placeholder="Search by Mobile"
                                className="form-control form-control-sm"
                                value={searchMobile}
                                onChange={e => setSearchMobile(e.target.value)}
                            />
                        </div>

                        <div className="d-flex gap-2">
                            <Link to="/add-cash-loan" className="btn btn-sm btn-warning-600 radius-8 d-inline-flex align-items-center gap-1">
                                <Icon icon="simple-line-icons:plus" className="text-xl" /> Add New Cash Loan
                            </Link>

                            <button
                                onClick={handleExport}
                                className="btn btn-sm btn-success radius-8 d-inline-flex align-items-center gap-1"
                            >
                                <Icon icon="simple-line-icons:cloud-download" className="text-xl" />
                                Export to Excel
                            </button>
                        </div>
                    </div>
                    <div className="card-body table-responsive">
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table text-sm" id="invoice-table">
                                <thead>
                                    <tr>
                                        <th scope="col" className="text-sm">#</th>
                                        <th scope="col" className="text-sm">Member</th>
                                        <th scope="col" className="text-sm">Branch</th>
                                        <th scope="col" className="text-sm">Staff</th>
                                        <th scope="col" className="text-sm">Loan AMT.</th>
                                        <th scope="col" className="text-sm">Loan AMT.(int)</th>
                                        <th scope="col" className="text-sm">Remaining</th>
                                        <th scope="col" className="text-sm">Group</th>
                                        <th scope="col" className="text-sm">EMI</th>
                                        <th scope="col" className="text-sm">Opening Date</th>
                                        <th scope="col" className="text-sm">Close Date</th>
                                        <th scope="col" className="text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLoans.length > 0 ? (
                                        filteredLoans.map((loan, index) => (
                                            <tr key={loan.id}>
                                                <td>{index + 1}</td>
                                                <td>{loan.member?.firstname} {loan.member?.lastname}</td>
                                                <td>{loan.branch?.branchname}</td>
                                                <td>{loan.staff?.firstname}</td>
                                                <td>{loan.loan_amount}</td>
                                                <td>{loan.loan_amount_with_interest}</td>
                                                <td>{loan.balance}</td>
                                                <td>{loan.group?.groupname}</td>
                                                <td>{Math.round(loan.loan_amount_with_interest / loan.tenure)}</td>
                                                <td>{loan.open_date}</td>
                                                <td>{loan.close_day ? loan.close_day : getCloseDate(loan.open_date, loan.tenure)}</td>
                                                <td className="d-flex gap-2">
                                                    <td className="d-flex gap-2">
                                                        <button
                                                            onClick={() => handleStatementPrintClick(loan)}

                                                            title="Generate Passbook"
                                                            className="btn btn-link p-0"
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <Icon icon="mdi:book-open-variant" className="text-primary text-xl" />
                                                        </button>



                                                        <Link to={`/add-cash-loan/${loan.id}`} title="Edit">
                                                            <Icon icon="lucide:edit" className="text-info text-xl cursor-pointer" />
                                                        </Link>

                                                        <button onClick={() => handleDelete(loan.id)} title="Delete" className="btn btn-link p-0">
                                                            <Icon icon="fluent:delete-24-regular" className="text-danger text-xl cursor-pointer" />
                                                        </button>

                                                        <Link to={`/cash-loan-payment/${loan.id}`} title="Pay EMI">
                                                            <Icon icon="material-symbols:currency-rupee" className="text-success text-xl cursor-pointer" />
                                                        </Link>

                                                        <button
                                                            onClick={() => handlePrintClick(loan)}
                                                            title="Print Loan Document"
                                                            className="btn btn-link p-0"
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <Icon icon="material-symbols:print" className="text-primary text-xl" />
                                                        </button>
                                                    </td>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No loans found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>


            {selectedLoanForPrint && (
                <div className="generate-passbook-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto', padding: '30px',
                }}>
                    <div className="print-loan-doc-container" style={{
                        maxWidth: '900px', margin: 'auto', backgroundColor: '#fff',
                        padding: '30px', borderRadius: '10px', fontFamily: 'monospace', color: '#000'
                    }}>
                        <h6 style={{ textAlign: 'center', borderBottom: '2px solid black', paddingBottom: '10px' }}>Loan Passbook</h6>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                            <tbody>
                                <tr><td><strong>Member Name:</strong></td><td>{selectedLoanForPrint.member?.firstname} {selectedLoanForPrint.member?.lastname}</td></tr>
                                <tr><td><strong>Husband Name:</strong></td><td>{selectedLoanForPrint.member?.husband_name || '-'}</td></tr>
                                <tr><td><strong>Address:</strong></td><td>{selectedLoanForPrint.member?.address || '-'}</td></tr>
                                <tr><td><strong>Aadhar No.:</strong></td><td>{selectedLoanForPrint.member?.aadhar_number || '-'}</td></tr>
                                <tr><td><strong>Mobile No.:</strong></td><td>{selectedLoanForPrint.member?.mobile || '-'}</td></tr>
                                <tr><td><strong>Disburse Date:</strong></td><td>{selectedLoanForPrint.open_date}</td></tr>
                                <tr><td><strong>Duration (weeks):</strong></td><td>{selectedLoanForPrint.tenure}</td></tr>
                                <tr><td><strong>Loan Amount:</strong></td><td>₹{selectedLoanForPrint.loan_amount_with_interest}</td></tr>
                                <tr><td><strong>EMI:</strong></td><td>₹{(selectedLoanForPrint.loan_amount / selectedLoanForPrint.tenure).toFixed(2)}</td></tr>
                                <tr><td><strong>Maturity Date:</strong></td><td>{getCloseDate(selectedLoanForPrint.open_date, selectedLoanForPrint.tenure)}</td></tr>
                            </tbody>
                        </table>

                        <h6 style={{ marginTop: '30px' }}>EMI Schedule</h6>
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid black', fontSize: '12px' }}>
                            <thead>
                                <tr style={{ background: '#ddd' }}>
                                    <th>S.NO</th>
                                    <th>DATE</th>
                                    <th>EMI</th>
                                    <th>DEPOSIT AMT.</th>
                                    <th>REMAINING AMT.</th>
                                    <th>REMARKS</th>
                                    <th>APPLICANT SIGN</th>
                                    <th>F-O SIGN</th>
                                </tr>
                            </thead>
                            <tbody>

                                {Array.from({ length: selectedLoanForPrint.tenure }).map((_, idx) => {
                                    const emi = (selectedLoanForPrint.loan_amount_with_interest / selectedLoanForPrint.tenure).toFixed(2);
                                    const emiDate = new Date(selectedLoanForPrint.open_date);
                                    emiDate.setDate(emiDate.getDate() + idx * 7);
                                    const formattedDate = emiDate.toISOString().split('T')[0];

                                    const payment = sortedPayments[idx];
                                    const depositAmt = payment ? parseFloat(payment.amount) : 0;

                                    totalPaid += depositAmt;
                                    const remainingAmt = (selectedLoanForPrint.loan_amount_with_interest - totalPaid).toFixed(2);

                                    return (
                                        <tr key={idx}>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>{formattedDate}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>₹{emi}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>₹{depositAmt.toFixed(2)}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>₹{remainingAmt}</td>
                                            <td style={{ border: '1px solid black' }}>{payment?.notes || ''}</td>
                                            <td style={{ border: '1px solid black' }}></td>
                                            <td style={{ border: '1px solid black' }}></td>
                                        </tr>
                                    );
                                })}


                                {/* {Array.from({ length: selectedLoanForPrint.tenure }).map((_, idx) => {
                                    const emi = (selectedLoanForPrint.loan_amount_with_interest / selectedLoanForPrint.tenure).toFixed(2);
                                    const disburseDate = new Date(selectedLoanForPrint.open_date);
                                    disburseDate.setDate(disburseDate.getDate() + idx * 7);
                                    const formattedDate = disburseDate.toISOString().split('T')[0];
                                    const remainingAmt = (selectedLoanForPrint.loan_amount_with_interest - (emi * (idx + 1))).toFixed(2);

                                    return (
                                        <tr key={idx}>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>{formattedDate}</td>
                                            <td style={{ border: '1px solid black', textAlign: 'center' }}>₹{emi}</td>
                                            <td style={{ border: '1px solid black' }}></td>
                                            <td style={{ border: '1px solid black' }}>₹{remainingAmt}</td>
                                            <td style={{ border: '1px solid black' }}></td>
                                            <td style={{ border: '1px solid black' }}></td>
                                            <td style={{ border: '1px solid black' }}></td>
                                        </tr>
                                    );
                                })} */}
                            </tbody>
                        </table>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                            <button onClick={handleCloseStatementView} style={{
                                padding: '10px 20px', backgroundColor: '#6c757d', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer'
                            }}>Close</button>

                            <button onClick={handlePrintDocument} style={{
                                padding: '10px 20px', backgroundColor: '#007bff', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer'
                            }}>Print as PDF</button>
                        </div>
                    </div>

                    <style>{`
            @media print {
                body * {
                    visibility: hidden;
                }
                .print-loan-doc-container, .print-loan-doc-container * {
                    visibility: visible;
                }
                .print-loan-doc-container {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    margin: 0;
                    box-shadow: none;
                }
                .print-loan-doc-overlay {
                    background: none !important;
                    padding: 0 !important;
                }
                button {
                    display: none;
                }
            }
        `}</style>
                </div>
            )}


            {printLoan && (
                <div className="print-loan-doc-overlay" style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 9999,
                    overflowY: 'auto',
                    padding: '30px',
                }}>
                    <div className="print-loan-doc-container" style={{
                        maxWidth: '850px',
                        margin: 'auto',
                        backgroundColor: '#fff',
                        padding: '30px',
                        borderRadius: '10px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        color: '#333',
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                            <img
                                src="/your-company-logo.png"
                                alt="Company Logo"
                                style={{ height: '80px', marginRight: '20px' }}
                            />
                            <div>
                                <h5 style={{ margin: 0, fontWeight: '600' }}>Your Company Name</h5>
                                <p style={{ margin: 0 }}>123 Finance St., Business City, Country</p>
                                <p style={{ margin: 0 }}>Phone: +1 234 567 890 | Email: info@yourcompany.com</p>
                            </div>
                        </div>

                        {/* Title */}
                        <h4 style={{
                            textAlign: 'center',
                            marginBottom: '25px',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            borderBottom: '2px solid #007bff',
                            paddingBottom: '10px',
                            fontWeight: '600'
                        }}>
                            Loan Opening Document
                        </h4>

                        {/* Loan Details Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                            <tbody>
                                {[
                                    { label: 'Loan ID', value: printLoan.loan_id },
                                    { label: 'Member', value: `${printLoan.member?.firstname} ${printLoan.member?.lastname}` },
                                    { label: 'Branch', value: printLoan.branch?.branchname },
                                    { label: 'Staff', value: printLoan.staff?.firstname },
                                    { label: 'Amount with Int', value: `₹${printLoan.loan_amount_with_interest.toLocaleString()}` },
                                    { label: 'Loan Amount', value: `₹${printLoan.loan_amount.toLocaleString()}` },
                                    { label: 'Tenure (Weeks)', value: printLoan.tenure },
                                    { label: 'EMI', value: `₹${(printLoan.loan_amount / printLoan.tenure).toFixed(2)}` },
                                    { label: 'Open Date', value: printLoan.open_date },
                                    {
                                        label: 'Close Date',
                                        value: printLoan.close_day || getCloseDate(printLoan.open_date, printLoan.tenure),
                                    },
                                    { label: 'Status', value: printLoan.status },
                                ].map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ padding: '10px', border: '1px solid #ddd', fontWeight: '600', width: '40%' }}>{item.label}</td>
                                        <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Signature Section */}
                        <div style={{ marginTop: '50px' }}>
                            <table style={{ width: '100%', textAlign: 'center', marginTop: '40px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ paddingBottom: '60px' }}>Customer Signature</th>
                                        <th style={{ paddingBottom: '60px' }}>FO Signature</th>
                                        <th style={{ paddingBottom: '60px' }}>Branch Manager Signature</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>________________________</td>
                                        <td>________________________</td>
                                        <td>________________________</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                            <button
                                onClick={handleClosePrintView}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }}
                            >
                                Close
                            </button>
                            <button
                                onClick={handlePrintDocument}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#007bff',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }}
                            >
                                Print Document
                            </button>
                        </div>
                    </div>

                    {/* Print styles */}
                    <style>{`
      @media print {
        body * {
          visibility: hidden;
        }
        .print-loan-doc-container, .print-loan-doc-container * {
          visibility: visible;
        }
        .print-loan-doc-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          margin: 0;
          box-shadow: none;
        }
        .print-loan-doc-overlay {
          background: none !important;
          padding: 0 !important;
        }
        button {
          display: none;
        }
      }
    `}</style>
                </div>
            )}


        </div>
    );
};


export default CashLoanLayer;